import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import clsx from 'clsx';
import { getScrollBehavior } from 'core/utils/motion';
import { guidLibGenerator } from 'core/utils/utils';
import { Icon } from 'ui';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';
import AccordionHeaderToggle, { AccordionItemContentIdContext } from './AccordionHeaderToggle';

const AccordionList2 = ({ arrowView, tabs, onGetLessonItemMessages, onChangeOpenedTabs, shouldScrollIntoViewOnExpand, style }) => {
  const instanceId = useRef(guidLibGenerator()).current;
  const accordionRefs = useRef([]);
  // Estado de los items con `hasInnerToggle`, por índice como el `key` del Accordion: su conmutador
  // no puede leer el contexto interno de MUI (el bundler resuelve la build ESM y el import directo la CJS)
  const [innerToggleExpanded, setInnerToggleExpanded] = useState({});

  function isItemExpanded(item, index) {
    return item.expanded ?? innerToggleExpanded[index] ?? Boolean(item.defaultExpanded);
  }

  async function onChangeAccordion(e, expanded, item, index) {
    if (item.hasInnerToggle) {
      setInnerToggleExpanded((previous) => ({ ...previous, [index]: expanded }));
    }

    if (onGetLessonItemMessages && expanded && item?.assignment?.guid) {
      await onGetLessonItemMessages(item.assignment);
    }

    onChangeOpenedTabs(item.id, expanded);
  }

  // Once the expand transition finishes the layout has its final height, so the submodules
  // hidden below the fold are scrolled into view. Fires only on expand (onEntered), never on
  // collapse, and not for defaultExpanded panels on mount (MUI skips the transition there).
  function onAccordionEntered(index) {
    accordionRefs.current[index]?.scrollIntoView({ behavior: getScrollBehavior(), block: 'start' });
  }

  return (
    <div className={clsx('accordion-list2', `accordion-list2--arrow-${arrowView}`, { 'accordion-list2--simple': style === 'simple' })}>
      {tabs.map((item, i) => {
        const headerId = `${instanceId}-panel-${i}-header`;
        const contentId = `${instanceId}-panel-${i}-content`;
        // La cabecera contiene otros controles y el conmutador es AccordionHeaderToggle
        const summaryProps = item.hasInnerToggle
          ? {
              'aria-disabled': undefined,
              'aria-expanded': undefined,
              className: 'accordion-list2__summary--inner-toggle',
              id: headerId,
              role: undefined,
              tabIndex: -1,
            }
          : { id: headerId, ...(item.isHeaderDecorative && { 'aria-hidden': true, className: 'accordion-list2__summary--decorative', tabIndex: -1 }) };

        return (
          <Accordion
            onClick={item.onClick ? item.onClick : undefined}
            key={i}
            ref={(el) => {
              accordionRefs.current[i] = el;
            }}
            className={item.className}
            defaultExpanded={item.defaultExpanded}
            disabled={item.disabled}
            expanded={item.expanded !== undefined ? item.expanded : undefined}
            id={item.id}
            onChange={(e, expanded) => {
              onChangeAccordion(e, expanded, item, i);
            }}
            TransitionProps={shouldScrollIntoViewOnExpand ? { onEntered: () => onAccordionEntered(i) } : undefined}
          >
            <AccordionSummary
              expandIcon={item.isHeaderDecorative ? undefined : <Icon name="chevron-down" size="xl" />}
              aria-controls={contentId}
              {...summaryProps}
            >
              {item.hasInnerToggle && <AccordionHeaderToggle contentId={contentId} isDisabled={Boolean(item.disabled)} isExpanded={isItemExpanded(item, i)} />}
              <AccordionItemContentIdContext.Provider value={contentId}>{item.title}</AccordionItemContentIdContext.Provider>
            </AccordionSummary>
            <AccordionDetails>{item.children}</AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
};

AccordionList2.propTypes = {
  arrowView: PropTypes.string,
  onChangeOpenedTabs: PropTypes.func,
  onGetLessonItemMessages: PropTypes.func,
  shouldScrollIntoViewOnExpand: PropTypes.bool,
  style: PropTypes.string, //'default' | 'simple'
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      assignment: PropTypes.shape({ guid: PropTypes.string }),
      children: PropTypes.node,
      className: PropTypes.string,
      defaultExpanded: PropTypes.bool,
      disabled: PropTypes.bool,
      expanded: PropTypes.bool,
      id: PropTypes.string,
      // Cabecera sin contenido visible ni chevron, fuera del Tab y del árbol accesible; exige `expanded: true`
      isHeaderDecorative: PropTypes.bool,
      // La cabecera contiene otros controles: el conmutador es AccordionHeaderToggle y el título lleva useAccordionItemLabelId()
      hasInnerToggle: PropTypes.bool,
      title: PropTypes.node,
    })
  ).isRequired,
};

AccordionList2.defaultProps = {
  arrowView: 'right',
  shouldScrollIntoViewOnExpand: false,
  tabs: [],
  onChangeOpenedTabs: () => null,
};

export default withTranslation('course')(AccordionList2);
